import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Send,
  Copy,
  Check,
  Trash2,
  Mail,
  ExternalLink,
  Loader2,
  Eye,
  Plus,
  RefreshCw,
  AlertCircle,
  ShieldCheck,
  CheckCircle,
  Clock,
  Download,
  X,
  CreditCard,
  FileSpreadsheet,
  FileSignature,
  FileCheck2,
} from 'lucide-react';
import type { Lead, FormInvite, FormSubmission, RateAgreementLetterFields, LeadDocument } from '../types';
import { authedFetch, downloadLeadDocument, type UserRole } from '../lib/db';

interface LeadFormsTabProps {
  lead: Lead;
  userRole?: UserRole;
  currentUserEmail?: string;
  showToast?: (msg: string, leadId?: string) => void;
  onStatusAdvanced?: (newStatus: string) => void;
  onActivityLogUpdated?: (activityLog: any[]) => void;
  onDocumentsUpdated?: (documents: LeadDocument[]) => void;
}

const SALESPERSON_DIRECTORY: { name: string; email: string }[] = [
  { name: 'Danean Allan', email: 'danean@armellini.com' },
  { name: 'David Armellini', email: 'darmellini@armellini.com' },
  { name: 'Hilary Dedrick', email: 'hdedrick@armellini.com' },
  { name: 'Fernando Perez', email: 'fperez@armellini.com' },
  { name: 'Sarah Nesbitt', email: 'snesbitt@armellini.com' },
  { name: 'Meybeth Rodriguez', email: 'm_rodriguez@armellini.com' },
];

function resolveSalespersonFromLeadOwner(owner?: string, fallbackEmail?: string) {
  const trimmed = (owner || '').trim();
  if (!trimmed) {
    return {
      name: 'Armellini Sales Team',
      email: fallbackEmail || 'sales@armellini.com',
    };
  }
  const match = SALESPERSON_DIRECTORY.find(
    (s) => s.name.toLowerCase() === trimmed.toLowerCase() || s.email.toLowerCase() === trimmed.toLowerCase()
  );
  if (match) return match;
  if (trimmed.includes('@')) {
    return { name: trimmed.split('@')[0], email: trimmed };
  }
  return {
    name: trimmed,
    email: fallbackEmail || 'sales@armellini.com',
  };
}

const FORM_OPTIONS: { type: FormInvite['formType']; label: string; desc: string; icon: React.ComponentType<{ className?: string }> }[] = [
  {
    type: 'credit_application',
    label: 'Credit Application & Agreement',
    desc: 'Company information, trade/bank references, receiving profile, and signature.',
    icon: FileText,
  },
  {
    type: 'w9',
    label: 'IRS Form W-9',
    desc: 'Request for Taxpayer Identification Number and Certification.',
    icon: FileCheck2,
  },
  {
    type: 'credit_card_authorization',
    label: 'Credit Card Authorization',
    desc: 'PCI-compliant cardholder authorization with masked tokenization.',
    icon: CreditCard,
  },
  {
    type: 'rate_agreement_letter',
    label: 'Rate Agreement Letter (DocuSeal)',
    desc: 'Custom carrier rate agreement letter with quote terms and digital acceptance via DocuSeal.',
    icon: FileSignature,
  },
];

export default function LeadFormsTab({
  lead,
  userRole = 'admin',
  currentUserEmail,
  showToast,
  onStatusAdvanced,
  onActivityLogUpdated,
  onDocumentsUpdated,
}: LeadFormsTabProps) {
  const [invites, setInvites] = useState<FormInvite[]>([]);
  const [submissions, setSubmissions] = useState<FormSubmission[]>([]);
  const [documents, setDocuments] = useState<LeadDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [syncingId, setSyncingId] = useState<string | null>(null);

  // Send modal state
  const [isSendOpen, setIsSendOpen] = useState(false);
  const [selectedFormType, setSelectedFormType] = useState<FormInvite['formType']>('credit_application');
  const [signerName, setSignerName] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [instructions, setInstructions] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [useDocuseal, setUseDocuseal] = useState(true);
  const [sendDocusealEmail, setSendDocusealEmail] = useState(true);
  const [submittingInvite, setSubmittingInvite] = useState(false);

  // Rate Agreement extra fields (matches PDF- Rate Agreement Template.md parameters)
  const [raFields, setRaFields] = useState<RateAgreementLetterFields>({
    proposalDate: new Date().toLocaleDateString('en-US'),
    billingEmail: '',
    accountNumber: '',
    destinationCity: '',
    destinationState: '',
    destinationZip: '',
    consigneeName: '',
    consigneeCity: '',
    consigneeState: '',
    consigneeZip: '',
    outboundState: 'FL',
    dos: 'Mon, Wed, Fri',
    firstShipmentDate: 'Upon Account Activation',
    minimumShipmentAmount: '$125.00',
    dollarAmount: '$95.00',
    unitOfMeasure: 'Pallet',
    handlingFee: '$0.00',
    paymentTerms: 'Net 15 Days',
    paymentTimeframe: '15 days from invoice date',
    lateFeePercentage: '1.5%',
    senderEmail: currentUserEmail || 'ccenter@armellini.com',
    senderName: 'Armellini Logistics Quoting Department',
  });

  // View submission modal
  const [activeSubmission, setActiveSubmission] = useState<FormSubmission | null>(null);

  const canManageForms = userRole === 'admin' || userRole === 'editor' || userRole === 'sales';
  const canViewFinancials = userRole === 'admin' || userRole === 'editor';

  const fetchForms = useCallback(async () => {
    if (!lead?.id) return;
    try {
      setLoading(true);
      const res = await authedFetch(`/api/leads/${lead.id}/forms`);
      if (res.ok) {
        const data = await res.json();
        setInvites(data.invites || []);
        setSubmissions(data.submissions || []);
        setDocuments(data.documents || []);
        if (Array.isArray(data.activityLog) && onActivityLogUpdated) {
          onActivityLogUpdated(data.activityLog);
        }
        if (Array.isArray(data.documents) && onDocumentsUpdated) {
          onDocumentsUpdated(data.documents);
        }
      }
    } catch (err) {
      console.error('Failed to load forms for lead:', err);
    } finally {
      setLoading(false);
    }
  }, [lead?.id, onActivityLogUpdated, onDocumentsUpdated]);

  useEffect(() => {
    fetchForms();
  }, [fetchForms]);

  // Reset modal defaults when opening
  const handleOpenSendModal = () => {
    setCompanyName(lead.company || '');
    setSignerName(lead.contactPerson || lead.name || '');
    setCustomerEmail(lead.email || '');
    setInstructions('');
    setSendEmail(true);
    setUseDocuseal(true);
    setSendDocusealEmail(true);

    const resolvedBillToType: 'Bill To/Consignee' | 'Bill To/Shipper' | 'Bill To' =
      lead.billToType ||
      (lead.company &&
      lead.shipperName &&
      lead.company.trim().toUpperCase() === lead.shipperName.trim().toUpperCase()
        ? 'Bill To/Shipper'
        : 'Bill To/Consignee');

    const formattedProposalDate = lead.proposalDate
      ? (() => {
          const d = new Date(`${lead.proposalDate}T12:00:00`);
          return isNaN(d.getTime()) ? lead.proposalDate : d.toLocaleDateString('en-US');
        })()
      : new Date().toLocaleDateString('en-US');

    const resolvedSalesperson = resolveSalespersonFromLeadOwner(lead.owner, currentUserEmail);

    setRaFields({
      billToType: resolvedBillToType,
      proposalDate: formattedProposalDate,
      billingEmail: lead.email || '',
      accountNumber:
        lead.accountNumber ||
        (resolvedBillToType === 'Bill To/Shipper' ? lead.shipperAccount : lead.consigneeAccount) ||
        '',
      destinationCity: lead.city || 'Miami',
      destinationState: lead.state || 'FL',
      destinationZip: lead.zipCode || '',
      consigneeName: lead.consigneeName || lead.company || '',
      consigneeAccount: lead.consigneeAccount || lead.accountNumber || '',
      consigneeCity: lead.consigneeCity || lead.city || '',
      consigneeState: lead.consigneeState || lead.state || '',
      consigneeZip: lead.consigneeZip || lead.zipCode || '',
      outboundState: lead.outboundStates?.[0] || 'FL',
      dos: lead.daysOfService?.length ? lead.daysOfService.join(', ') : 'Mon, Wed, Fri',
      firstShipmentDate: lead.firstShipmentDate || 'Upon Account Activation',
      minimumShipmentAmount: lead.minimumShipmentAmount
        ? `$${Number(lead.minimumShipmentAmount).toFixed(2)}`
        : '$125.00',
      dollarAmount: lead.quoteDollarAmount
        ? `$${Number(lead.quoteDollarAmount).toFixed(2)}`
        : lead.dealValue
        ? `$${Number(lead.dealValue).toFixed(2)}`
        : '$95.00',
      unitOfMeasure: lead.uom || 'Pallet',
      handlingFee: lead.noHandlingFee
        ? '$0.00'
        : lead.handlingFeeAmount
        ? `$${Number(lead.handlingFeeAmount).toFixed(2)}`
        : '$0.00',
      paymentTerms: lead.paymentTerms || 'Net 15 Days',
      paymentTimeframe: '15 days from invoice date',
      lateFeePercentage: '1.5%',
      senderEmail: resolvedSalesperson.email,
      senderName: resolvedSalesperson.name,
    });

    setIsSendOpen(true);
  };

  const handleCopyLink = async (invite: FormInvite) => {
    const url = invite.docusealSigningUrl || `${window.location.origin}/apply/${invite.id}`;
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(invite.id);
      if (showToast) showToast('Signing link copied to clipboard!');
      setTimeout(() => setCopiedId(null), 2500);
    } catch {
      prompt('Copy this link:', url);
    }
  };

  const handleSyncDocuseal = async (inviteId: string) => {
    try {
      setSyncingId(inviteId);
      const res = await authedFetch(`/api/leads/${lead.id}/forms/${inviteId}/sync-docuseal`, {
        method: 'POST',
      });
      const data = await res.json();
      if (res.ok) {
        if (data.completed) {
          if (showToast) showToast('Rate Agreement signature confirmed & signed PDF downloaded!');
        } else {
          if (showToast) showToast(`DocuSeal Status: ${data.status || 'Sent (Pending Signature)'}`);
        }
        await fetchForms();
      } else {
        if (showToast) showToast(`Sync failed: ${data.error || 'Server error'}`);
      }
    } catch (err: any) {
      if (showToast) showToast(`Error: ${err.message}`);
    } finally {
      setSyncingId(null);
    }
  };

  const handleResendInvite = async (inviteId: string) => {
    try {
      setResendingId(inviteId);
      const res = await authedFetch(`/api/leads/${lead.id}/forms/${inviteId}/resend`, {
        method: 'POST',
      });
      if (res.ok) {
        if (showToast) showToast('Form invitation email resent successfully!');
        await fetchForms();
      } else {
        const err = await res.json().catch(() => ({}));
        if (showToast) showToast(`Failed to resend: ${err.error || 'Server error'}`);
      }
    } catch (err: any) {
      if (showToast) showToast(`Error: ${err.message}`);
    } finally {
      setResendingId(null);
    }
  };

  const handleRevokeInvite = async (inviteId: string) => {
    if (!confirm('Are you sure you want to cancel and revoke this form invite? The customer will no longer be able to open or submit it.')) {
      return;
    }
    try {
      setRevokingId(inviteId);
      const res = await authedFetch(`/api/leads/${lead.id}/forms/${inviteId}`, {
        method: 'DELETE',
      });
      if (res.ok) {
        if (showToast) showToast('Form invite revoked.');
        await fetchForms();
      } else {
        const err = await res.json().catch(() => ({}));
        if (showToast) showToast(`Failed to revoke: ${err.error || 'Server error'}`);
      }
    } catch (err: any) {
      if (showToast) showToast(`Error: ${err.message}`);
    } finally {
      setRevokingId(null);
    }
  };

  const handleCreateInvite = async (e?: React.FormEvent | React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (!companyName.trim() || !customerEmail.trim()) {
      alert('Company name and customer email are required.');
      return;
    }

    try {
      setSubmittingInvite(true);
      const payload = {
        formType: selectedFormType,
        companyName: companyName.trim(),
        signerName: signerName.trim() || companyName.trim(),
        customerEmail: customerEmail.trim(),
        instructions: instructions.trim() || undefined,
        sendEmail,
        useDocuseal: selectedFormType === 'rate_agreement_letter' ? useDocuseal : false,
        sendDocusealEmail: selectedFormType === 'rate_agreement_letter' ? sendDocusealEmail : false,
        rateAgreementFields: selectedFormType === 'rate_agreement_letter' ? raFields : undefined,
      };

      const res = await authedFetch(`/api/leads/${lead.id}/forms/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to create invite');
      }

      const data = await res.json();
      setIsSendOpen(false);
      if (showToast) {
        showToast(
          selectedFormType === 'rate_agreement_letter' && useDocuseal
            ? `Rate Agreement created on DocuSeal & sent to ${customerEmail.trim()}!`
            : sendEmail
            ? `Form invitation sent to ${customerEmail.trim()}!`
            : 'Form link generated successfully! You can copy it now.'
        );
      }
      await fetchForms();
    } catch (err: any) {
      alert(`Error creating form invite: ${err.message}`);
    } finally {
      setSubmittingInvite(false);
    }
  };

  const getFormLabel = (type: FormInvite['formType']) => {
    switch (type) {
      case 'credit_application':
        return 'Credit Application';
      case 'w9':
        return 'IRS Form W-9';
      case 'credit_card_authorization':
        return 'Credit Card Auth';
      case 'rate_agreement_letter':
        return 'Rate Agreement Letter';
      default:
        return type;
    }
  };

  const getStatusBadge = (status: FormInvite['status']) => {
    switch (status) {
      case 'completed':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-emerald-100 dark:bg-emerald-950/70 text-emerald-800 dark:text-emerald-300 border border-emerald-300 dark:border-emerald-800">
            <CheckCircle className="w-3 h-3 text-emerald-600 dark:text-emerald-400" /> Completed
          </span>
        );
      case 'opened':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-purple-100 dark:bg-purple-950/70 text-purple-800 dark:text-purple-300 border border-purple-300 dark:border-purple-800">
            <Eye className="w-3 h-3 text-purple-600 dark:text-purple-400" /> Opened
          </span>
        );
      case 'sent':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-100 dark:bg-blue-950/70 text-blue-800 dark:text-blue-300 border border-blue-300 dark:border-blue-800">
            <Send className="w-3 h-3 text-blue-600 dark:text-blue-400" /> Sent
          </span>
        );
      case 'revoked':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 border border-slate-300 dark:border-slate-700">
            Revoked
          </span>
        );
      case 'expired':
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-bold bg-rose-100 dark:bg-rose-950/70 text-rose-800 dark:text-rose-300 border border-rose-300 dark:border-rose-800">
            Expired
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {/* Header bar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 pb-3">
        <div>
          <h3 className="text-xs font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest flex items-center gap-1.5">
            <FileSpreadsheet className="w-4 h-4" /> Lead Online Forms
          </h3>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
            Credit applications, W-9 tax forms, card authorizations, and rate letters sent to{' '}
            <span className="font-semibold text-slate-700 dark:text-slate-200">{lead.company}</span>.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={fetchForms}
            disabled={loading}
            className="p-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-500 hover:text-slate-800 dark:hover:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 transition cursor-pointer"
            title="Refresh forms"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </button>

          {canManageForms && (
            <button
              type="button"
              onClick={handleOpenSendModal}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" /> Send Online Form
            </button>
          )}
        </div>
      </div>

      {/* Main List */}
      {loading ? (
        <div className="p-12 text-center">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600 mx-auto mb-2" />
          <p className="text-xs text-slate-400">Loading form history...</p>
        </div>
      ) : invites.length === 0 ? (
        <div className="border border-slate-200 dark:border-slate-800 rounded-xl p-8 text-center bg-slate-50/40 dark:bg-slate-900/20">
          <FileText className="w-8 h-8 text-slate-300 dark:text-slate-700 mx-auto mb-2.5" />
          <h4 className="text-sm font-bold text-slate-700 dark:text-slate-300">No Forms Sent Yet</h4>
          <p className="text-xs text-slate-500 dark:text-slate-400 max-w-sm mx-auto mt-1 mb-4">
            Send a digital Credit Application, W-9, Credit Card Auth, or Rate Agreement directly to the prospect.
          </p>
          {canManageForms && (
            <button
              type="button"
              onClick={handleOpenSendModal}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-sm transition cursor-pointer"
            >
              <Plus className="w-3.5 h-3.5" /> Send First Form
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {invites.map((invite) => {
            const submission = submissions.find((s) => s.inviteId === invite.id);
            const isCompleted = invite.status === 'completed' || !!submission;

            return (
              <div
                key={invite.id}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-4 shadow-sm hover:border-slate-300 dark:hover:border-slate-700 transition flex flex-col gap-3"
              >
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-blue-50 dark:bg-blue-950/60 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                      {invite.formType === 'credit_application' ? (
                        <FileText className="w-4 h-4" />
                      ) : invite.formType === 'w9' ? (
                        <FileCheck2 className="w-4 h-4" />
                      ) : invite.formType === 'credit_card_authorization' ? (
                        <CreditCard className="w-4 h-4" />
                      ) : (
                        <FileSignature className="w-4 h-4" />
                      )}
                    </div>

                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs font-bold text-slate-800 dark:text-slate-200">
                          {getFormLabel(invite.formType)}
                        </span>
                        {getStatusBadge(invite.status)}
                        {invite.docusealSubmissionId && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-indigo-50 dark:bg-indigo-950/70 text-indigo-700 dark:text-indigo-300 border border-indigo-200 dark:border-indigo-800" title="DocuSeal Envelope ID">
                            DocuSeal #{invite.docusealSubmissionId}
                          </span>
                        )}
                      </div>
                      <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                        Recipient: <span className="font-semibold">{invite.signerName}</span> ({invite.customerEmail})
                      </p>
                    </div>
                  </div>

                  {/* Actions Bar */}
                  <div className="flex items-center gap-1.5 self-end sm:self-center">
                    {/* Copy Link Button */}
                    <button
                      type="button"
                      onClick={() => handleCopyLink(invite)}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 rounded-md transition cursor-pointer"
                      title={invite.docusealSigningUrl ? 'Copy DocuSeal signing link' : 'Copy public link'}
                    >
                      {copiedId === invite.id ? (
                        <>
                          <Check className="w-3 h-3 text-emerald-600" /> Copied
                        </>
                      ) : (
                        <>
                          <Copy className="w-3 h-3" /> Copy Link
                        </>
                      )}
                    </button>

                    {/* Open in new tab */}
                    <a
                      href={invite.docusealSigningUrl || `/apply/${invite.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-1.5 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 rounded-md hover:bg-slate-100 dark:hover:bg-slate-800 transition"
                      title={invite.docusealSigningUrl ? 'Open DocuSeal signing room' : 'Open form page'}
                    >
                      <ExternalLink className="w-3.5 h-3.5" />
                    </a>

                    {/* Sync DocuSeal button */}
                    {invite.docusealSubmissionId && !isCompleted && canManageForms && (
                      <button
                        type="button"
                        onClick={() => handleSyncDocuseal(invite.id)}
                        disabled={syncingId === invite.id}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 rounded-md transition cursor-pointer"
                        title="Check live DocuSeal signing status and fetch completed PDF"
                      >
                        <RefreshCw className={`w-3 h-3 ${syncingId === invite.id ? 'animate-spin' : ''}`} />
                        Sync DocuSeal
                      </button>
                    )}

                    {/* Resend button if not completed */}
                    {!isCompleted && canManageForms && (
                      <button
                        type="button"
                        onClick={() => handleResendInvite(invite.id)}
                        disabled={resendingId === invite.id}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/40 rounded-md transition cursor-pointer"
                        title="Resend invitation email"
                      >
                        {resendingId === invite.id ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Send className="w-3 h-3" />
                        )}
                        Resend
                      </button>
                    )}

                    {/* View Details / Submission if completed */}
                    {submission && (
                      <button
                        type="button"
                        onClick={() => setActiveSubmission(submission)}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-emerald-700 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-950/50 hover:bg-emerald-100 rounded-md transition cursor-pointer"
                      >
                        <Eye className="w-3 h-3" /> View Data
                      </button>
                    )}

                    {/* Download Signed PDF */}
                    {(submission?.signedPdfUrl || documents.find(d => d.leadId === invite.leadId && (d.documentType === 'Signed Agreement' || d.documentType === 'signed_agreement'))) && (
                      <button
                        type="button"
                        onClick={async () => {
                          const signedDoc = documents.find(
                            d => d.leadId === invite.leadId && (d.documentType === 'Signed Agreement' || d.documentType === 'signed_agreement')
                          );
                          const fileName = `${invite.formType}_${lead.company.replace(/\s+/g, '_')}.pdf`;
                          try {
                            if (signedDoc?.id) {
                              await downloadLeadDocument(lead.id, signedDoc.id, signedDoc.fileName || fileName);
                            } else if (submission?.signedPdfUrl?.startsWith('/api/leads/')) {
                              const parts = submission.signedPdfUrl.split('/');
                              const docId = parts[parts.length - 2];
                              await downloadLeadDocument(lead.id, docId, fileName);
                            } else if (submission?.signedPdfUrl) {
                              window.open(submission.signedPdfUrl, '_blank', 'noopener,noreferrer');
                            }
                          } catch (err: any) {
                            if (showToast) showToast(`Download failed: ${err.message}`);
                          }
                        }}
                        className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-semibold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-950/50 hover:bg-indigo-100 rounded-md transition cursor-pointer"
                        title="Download signed PDF"
                      >
                        <Download className="w-3 h-3" /> Signed PDF
                      </button>
                    )}

                    {/* Cancel / Revoke button */}
                    {!isCompleted && canManageForms && (
                      <button
                        type="button"
                        onClick={() => handleRevokeInvite(invite.id)}
                        disabled={revokingId === invite.id}
                        className="p-1.5 text-slate-400 hover:text-rose-600 dark:hover:text-rose-400 rounded-md hover:bg-rose-50 dark:hover:bg-rose-950/30 transition cursor-pointer"
                        title="Revoke / Cancel invite"
                      >
                        {revokingId === invite.id ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="w-3.5 h-3.5" />
                        )}
                      </button>
                    )}
                  </div>
                </div>

                {/* Audit footnote */}
                <div className="flex flex-wrap items-center gap-3 text-[10px] text-slate-400 dark:text-slate-500 font-mono pt-2 border-t border-slate-100 dark:border-slate-800">
                  <span>Created: {new Date(invite.createdAt).toLocaleString()}</span>
                  {invite.openedAt && (
                    <span className="text-purple-600 dark:text-purple-400">
                      Opened: {new Date(invite.openedAt).toLocaleString()}
                    </span>
                  )}
                  {invite.completedAt && (
                    <span className="text-emerald-600 dark:text-emerald-400">
                      Completed: {new Date(invite.completedAt).toLocaleString()}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── STORED SIGNED DOCUMENTS ──────────────────────────────────────────────── */}
      {documents.length > 0 && (
        <div className="pt-4 border-t border-slate-200 dark:border-slate-800 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5 text-indigo-500" />
              CRM Archived Documents ({documents.length})
            </h4>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {documents.map((doc) => (
              <div
                key={doc.id}
                className="flex items-center justify-between p-2.5 bg-slate-50 dark:bg-slate-900/60 border border-slate-200 dark:border-slate-800 rounded-lg text-xs"
              >
                <div className="flex items-center gap-2 min-w-0 pr-2">
                  <FileText className="w-4 h-4 text-slate-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-800 dark:text-slate-200 truncate" title={doc.fileName}>
                      {doc.fileName}
                    </p>
                    <p className="text-[10px] text-slate-400">
                      {new Date(doc.createdAt).toLocaleDateString()} • {(doc.fileSize / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    downloadLeadDocument(lead.id, doc.id, doc.fileName).catch((err) => {
                      if (showToast) showToast(`Download failed: ${err.message}`);
                    })
                  }
                  className="px-2.5 py-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700 rounded text-[11px] font-bold text-slate-700 dark:text-slate-300 transition shrink-0 inline-flex items-center gap-1 cursor-pointer"
                >
                  <Download className="w-3 h-3" /> Download
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ─── SEND FORM MODAL ──────────────────────────────────────────────────────── */}
      {isSendOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-800">
              <div className="flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-600" />
                <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">
                  Send Online Form to Prospect
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setIsSendOpen(false)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mt-4 space-y-4">
              {/* Form Type Selection */}
              <div>
                <label className="block text-xs font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                  Select Form Type <span className="text-rose-500">*</span>
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {FORM_OPTIONS.map((opt) => {
                    const Icon = opt.icon;
                    const isSelected = selectedFormType === opt.type;
                    return (
                      <button
                        key={opt.type}
                        type="button"
                        onClick={() => setSelectedFormType(opt.type)}
                        className={`text-left p-3 rounded-xl border transition cursor-pointer flex flex-col gap-1 ${
                          isSelected
                            ? 'border-blue-600 bg-blue-50/60 dark:bg-blue-950/40 text-blue-900 dark:text-blue-100 ring-2 ring-blue-500'
                            : 'border-slate-200 dark:border-slate-800 hover:border-slate-300 dark:hover:border-slate-700 bg-white dark:bg-slate-950'
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Icon className={`w-4 h-4 ${isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-slate-400'}`} />
                          <span className="text-xs font-bold">{opt.label}</span>
                        </div>
                        <p className="text-[10px] text-slate-500 dark:text-slate-400 line-clamp-2 leading-tight">
                          {opt.desc}
                        </p>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Recipient Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
                <div>
                  <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                    Company Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="w-full text-xs px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100"
                  />
                </div>

                <div>
                  <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                    Contact / Signer Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    className="w-full text-xs px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                  Recipient Email Address <span className="text-rose-500">*</span>
                </label>
                <input
                  type="email"
                  required
                  value={customerEmail}
                  onChange={(e) => setCustomerEmail(e.target.value)}
                  className="w-full text-xs px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100"
                />
              </div>

              {/* Rate Agreement Letter Extra fields */}
              {selectedFormType === 'rate_agreement_letter' && (
                <div className="bg-slate-50 dark:bg-slate-950/60 p-4 rounded-xl border border-slate-200 dark:border-slate-800 space-y-3.5">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-wider flex items-center gap-1.5">
                      <FileSignature className="w-3.5 h-3.5" />
                      Rate Agreement Parameters (DocuSeal Template)
                    </h4>
                    <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-indigo-100 dark:bg-indigo-950/60 text-indigo-700 dark:text-indigo-300">
                      sign.armellini.net
                    </span>
                  </div>

                  {/* DocuSeal Toggle Options */}
                  <div className="p-3 bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 space-y-2">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={useDocuseal}
                        onChange={(e) => setUseDocuseal(e.target.checked)}
                        className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                      />
                      <span className="text-xs font-semibold text-slate-800 dark:text-slate-200">
                        Process via Self-Hosted DocuSeal (Digital Signature & Audit Trail)
                      </span>
                    </label>
                    {useDocuseal && (
                      <label className="flex items-center gap-2 cursor-pointer select-none pl-6">
                        <input
                          type="checkbox"
                          checked={sendDocusealEmail}
                          onChange={(e) => setSendDocusealEmail(e.target.checked)}
                          className="w-4 h-4 rounded text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="text-xs text-slate-600 dark:text-slate-400">
                          Send signing invitation email directly from DocuSeal
                        </span>
                      </label>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                        Proposal Date
                      </label>
                      <input
                        type="text"
                        value={raFields.proposalDate || ''}
                        onChange={(e) => setRaFields({ ...raFields, proposalDate: e.target.value })}
                        placeholder="e.g. 10/26/2026"
                        className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                        Bill-To Role
                      </label>
                      <select
                        value={raFields.billToType || 'Bill To/Consignee'}
                        onChange={(e) =>
                          setRaFields({
                            ...raFields,
                            billToType: e.target.value as 'Bill To/Consignee' | 'Bill To/Shipper' | 'Bill To',
                          })
                        }
                        className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                      >
                        <option value="Bill To/Consignee">Bill To/Consignee (Single Box)</option>
                        <option value="Bill To/Shipper">Bill To/Shipper (+ Consignee Box)</option>
                        <option value="Bill To">Bill To</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                        Armellini Account #
                      </label>
                      <input
                        type="text"
                        value={raFields.accountNumber || ''}
                        onChange={(e) => setRaFields({ ...raFields, accountNumber: e.target.value })}
                        placeholder="e.g. B010081"
                        className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                        Outbound / Origin State
                      </label>
                      <input
                        type="text"
                        value={raFields.outboundState || ''}
                        onChange={(e) => setRaFields({ ...raFields, outboundState: e.target.value })}
                        placeholder="FL"
                        className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                        Days of Service (DOS)
                      </label>
                      <input
                        type="text"
                        value={raFields.dos || ''}
                        onChange={(e) => setRaFields({ ...raFields, dos: e.target.value })}
                        placeholder="Mon, Wed, Fri"
                        className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                        First Shipment Date
                      </label>
                      <input
                        type="text"
                        value={raFields.firstShipmentDate || ''}
                        onChange={(e) => setRaFields({ ...raFields, firstShipmentDate: e.target.value })}
                        placeholder="Upon Account Activation"
                        className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                        Rate / Dollar Amount
                      </label>
                      <input
                        type="text"
                        value={raFields.dollarAmount || ''}
                        onChange={(e) => setRaFields({ ...raFields, dollarAmount: e.target.value })}
                        placeholder="$95.00"
                        className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                        Unit of Measure
                      </label>
                      <input
                        type="text"
                        value={raFields.unitOfMeasure || ''}
                        onChange={(e) => setRaFields({ ...raFields, unitOfMeasure: e.target.value })}
                        placeholder="Pallet, Cube, Piece"
                        className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                        Minimum Shipment Amount
                      </label>
                      <input
                        type="text"
                        value={raFields.minimumShipmentAmount || ''}
                        onChange={(e) => setRaFields({ ...raFields, minimumShipmentAmount: e.target.value })}
                        placeholder="$125.00"
                        className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                        Handling Fee
                      </label>
                      <input
                        type="text"
                        value={raFields.handlingFee || ''}
                        onChange={(e) => setRaFields({ ...raFields, handlingFee: e.target.value })}
                        placeholder="$0.00"
                        className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                        Payment Terms
                      </label>
                      <input
                        type="text"
                        value={raFields.paymentTerms || ''}
                        onChange={(e) => setRaFields({ ...raFields, paymentTerms: e.target.value })}
                        placeholder="Net 15 Days"
                        className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                        Delivery City
                      </label>
                      <input
                        type="text"
                        value={raFields.destinationCity || ''}
                        onChange={(e) => setRaFields({ ...raFields, destinationCity: e.target.value })}
                        placeholder="Miami"
                        className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                        Delivery State
                      </label>
                      <input
                        type="text"
                        value={raFields.destinationState || ''}
                        onChange={(e) => setRaFields({ ...raFields, destinationState: e.target.value })}
                        placeholder="FL"
                        className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                        Delivery Zip Code
                      </label>
                      <input
                        type="text"
                        value={raFields.destinationZip || ''}
                        onChange={(e) => setRaFields({ ...raFields, destinationZip: e.target.value })}
                        placeholder="33166"
                        className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                        Salesperson Name
                      </label>
                      <input
                        type="text"
                        value={raFields.senderName || ''}
                        onChange={(e) => setRaFields({ ...raFields, senderName: e.target.value })}
                        placeholder="Danean Allan"
                        className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                        Salesperson Email
                      </label>
                      <input
                        type="email"
                        value={raFields.senderEmail || ''}
                        onChange={(e) => setRaFields({ ...raFields, senderEmail: e.target.value })}
                        placeholder="danean@armellini.com"
                        className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                      />
                    </div>
                  </div>

                  {raFields.billToType === 'Bill To/Shipper' && (
                    <div className="pt-2 border-t border-slate-200 dark:border-slate-800 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
                      <div className="md:col-span-2">
                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                          Consignee Company Name
                        </label>
                        <input
                          type="text"
                          value={raFields.consigneeName || ''}
                          onChange={(e) => setRaFields({ ...raFields, consigneeName: e.target.value })}
                          placeholder="Consignee Name"
                          className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                          Consignee Account #
                        </label>
                        <input
                          type="text"
                          value={raFields.consigneeAccount || ''}
                          onChange={(e) => setRaFields({ ...raFields, consigneeAccount: e.target.value })}
                          placeholder="e.g. C123456"
                          className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                          Consignee City
                        </label>
                        <input
                          type="text"
                          value={raFields.consigneeCity || ''}
                          onChange={(e) => setRaFields({ ...raFields, consigneeCity: e.target.value })}
                          placeholder="Miami"
                          className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                            State
                          </label>
                          <input
                            type="text"
                            value={raFields.consigneeState || ''}
                            onChange={(e) => setRaFields({ ...raFields, consigneeState: e.target.value })}
                            placeholder="FL"
                            className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                          />
                        </div>
                        <div>
                          <label className="block text-[10px] font-bold text-slate-500 uppercase mb-1">
                            Zip
                          </label>
                          <input
                            type="text"
                            value={raFields.consigneeZip || ''}
                            onChange={(e) => setRaFields({ ...raFields, consigneeZip: e.target.value })}
                            placeholder="33166"
                            className="w-full text-xs px-3 py-1.5 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900"
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Instructions / Notes */}
              <div>
                <label className="block text-[11px] font-bold text-slate-600 dark:text-slate-400 uppercase tracking-wider mb-1">
                  Custom Welcome Instructions (Optional)
                </label>
                <textarea
                  rows={2}
                  value={instructions}
                  onChange={(e) => setInstructions(e.target.value)}
                  placeholder="Add any specific instructions to display at the top of the form..."
                  className="w-full text-xs p-2.5 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100"
                />
              </div>

              {/* Options */}
              <div className="pt-2 border-t border-slate-100 dark:border-slate-800 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    className="w-4 h-4 rounded text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-xs font-semibold text-slate-700 dark:text-slate-300">
                    Send invitation email immediately to recipient
                  </span>
                </label>
              </div>

              <div className="flex items-center justify-end gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsSendOpen(false)}
                  className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg transition"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleCreateInvite}
                  disabled={submittingInvite}
                  className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-lg text-xs font-bold text-white bg-blue-600 hover:bg-blue-700 shadow-md transition cursor-pointer disabled:opacity-50"
                >
                  {submittingInvite ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating...
                    </>
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5" /> Create & Send Invite
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ─── SUBMISSION DETAILS MODAL ──────────────────────────────────────────────── */}
      {activeSubmission && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between pb-4 border-b border-slate-100 dark:border-slate-800">
              <div>
                <h3 className="text-base font-bold text-slate-900 dark:text-slate-100">
                  Form Submission Details
                </h3>
                <p className="text-xs text-slate-500">
                  Submitted by {activeSubmission.customerName} on{' '}
                  {new Date(activeSubmission.submittedAt).toLocaleString()}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setActiveSubmission(null)}
                className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 p-1 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mt-4 space-y-4 text-xs">
              {/* Audit Log Box */}
              <div className="bg-slate-50 dark:bg-slate-950 p-4 rounded-xl border border-slate-200 dark:border-slate-800">
                <h4 className="text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2 flex items-center gap-1.5">
                  <ShieldCheck className="w-4 h-4 text-emerald-600" /> Digital Signature Audit Trail
                </h4>
                <div className="space-y-2">
                  {(activeSubmission.auditLog || []).map((audit, idx) => (
                    <div key={idx} className="flex items-start justify-between gap-4 font-mono text-[10px]">
                      <div>
                        <span className="font-bold text-slate-800 dark:text-slate-200">{audit.action}:</span>{' '}
                        <span className="text-slate-600 dark:text-slate-400">{audit.details}</span>
                        {audit.ip && <span className="text-slate-400 ml-2">IP: {audit.ip}</span>}
                      </div>
                      <span className="text-slate-400 shrink-0">
                        {new Date(audit.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Form Data JSON / Formatted Viewer */}
              <div>
                <h4 className="text-[11px] font-bold text-slate-700 dark:text-slate-300 uppercase tracking-wider mb-2">
                  Captured Form Fields
                </h4>
                <div className="bg-slate-50 dark:bg-slate-950 p-4 rounded-xl border border-slate-200 dark:border-slate-800 max-h-60 overflow-y-auto font-mono text-[11px] text-slate-800 dark:text-slate-200">
                  <pre>{JSON.stringify(activeSubmission.formData, null, 2)}</pre>
                </div>
              </div>

              {/* Download PDF button */}
              {activeSubmission.signedPdfUrl && (
                <div className="pt-2 flex justify-end">
                  <a
                    href={activeSubmission.signedPdfUrl}
                    download={`Submission_${activeSubmission.id}.pdf`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-xl font-bold text-xs shadow-md transition"
                  >
                    <Download className="w-4 h-4" /> Download Signed PDF
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
